import React from "react";

import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Brain,
  BookOpen,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Download,
  Layers,
  Lightbulb,
  MessageSquare,
  Search,
  Sparkles,
  Tag,
  ThumbsDown,
  ThumbsUp,
  Users,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  getExperimentApiErrorMessage,
  resolvePrototypeIterationIdFromDetail,
  useExperimentByIdQuery,
  useExperimentInsights,
  usePrototypeByExperiment,
} from "../api/experimentsApi";
import { useFeedbackFlow } from "../api/feedbackFlowApi";
import { buildExperimentReportViewModel } from "../utils/experimentReport";
import {
  getSyntheticInsightsForExperiment,
  type SyntheticParticipantRow,
} from "../utils/syntheticInsights";

import type {
  ExperimentReportDetailsModel,
  ExperimentReportResultsModel,
  ReportAdditionalInsight,
  ReportInterviewQuestion,
  ReportResearchQuestion,
  ReportSupportingResponse,
} from "../utils/experimentReport";

type InsightTabId =
  | "research-questions"
  | "ai-insights"
  | "responses"
  | "telemetry";

const toLabel = (value?: string | null): string =>
  (value ?? "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const normalizeInsightsStatus = (
  status?: string | null,
): string => {
  const normalized = String(status ?? "").trim().toLowerCase();
  switch (normalized) {
    case "notgenerated":
    case "not_generated":
    case "not generated":
      return "notgenerated";
    case "generating":
    case "pending":
    case "queued":
    case "running":
    case "processing":
    case "in_progress":
      return "generating";
    case "generated":
    case "succeeded":
    case "success":
    case "completed":
    case "complete":
      return "generated";
    default:
      return normalized;
  }
};

const formatPercent = (value?: number | null): string => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "N/A";
  }

  return `${Math.round(value * 100)}%`;
};

const formatMetricValue = (
  value: number | string | null | undefined,
  suffix = "",
): string => {
  if (typeof value === "number") {
    return `${value.toLocaleString()}${suffix}`;
  }

  if (typeof value === "string" && value.trim()) {
    return value;
  }

  return "N/A";
};

const getStatusClasses = (status: ExperimentReportResultsModel["status"]) => {
  switch (status) {
    case "success":
      return "border-green-500/30 bg-green-500/15 text-green-400";
    case "pending":
      return "border-amber-500/30 bg-amber-500/15 text-amber-300";
    default:
      return "border-red-500/30 bg-red-500/15 text-red-400";
  }
};

const getSentimentClasses = (
  sentiment: "positive" | "negative" | "neutral",
) => {
  switch (sentiment) {
    case "positive":
      return "border-green-500/25 bg-green-500/15 text-green-400";
    case "negative":
      return "border-red-500/25 bg-red-500/15 text-red-400";
    default:
      return "border-amber-500/25 bg-amber-500/15 text-amber-400";
  }
};

const getInterviewSentimentLabel = (
  sentiment: "positive" | "negative" | "neutral",
): string => {
  switch (sentiment) {
    case "neutral":
      return "Mixed";
    default:
      return toLabel(sentiment);
  }
};

const getInsightAccentClasses = (
  sentiment: "positive" | "negative" | "neutral",
) => {
  switch (sentiment) {
    case "positive":
      return {
        border: "border-l-emerald-400",
        title: "text-emerald-300",
        icon: "text-emerald-300",
      };
    case "negative":
      return {
        border: "border-l-red-400",
        title: "text-red-300",
        icon: "text-red-300",
      };
    default:
      return {
        border: "border-l-amber-400",
        title: "text-amber-300",
        icon: "text-amber-300",
      };
  }
};

const getResearchQuestionAccentClasses = (
  sentiment: "positive" | "negative" | "neutral",
) => {
  switch (sentiment) {
    case "positive":
      return {
        border: "border-l-emerald-400",
        line: "border-emerald-400/45",
        icon: "text-emerald-300",
        title: "text-emerald-300",
      };
    case "negative":
      return {
        border: "border-l-red-400",
        line: "border-red-400/45",
        icon: "text-red-300",
        title: "text-red-300",
      };
    default:
      return {
        border: "border-l-amber-400",
        line: "border-amber-400/45",
        icon: "text-amber-300",
        title: "text-amber-300",
      };
  }
};

const getDecisionAccentClasses = (label: string) => {
  const normalized = label.trim().toLowerCase();

  if (normalized === "supported" || normalized === "pursue") {
    return {
      icon: "border-green-500/30 bg-green-500/15 text-green-400",
      bar: "bg-green-400",
    };
  }

  if (normalized === "not supported" || normalized === "pause") {
    return {
      icon: "border-red-500/30 bg-red-500/15 text-red-400",
      bar: "bg-red-400",
    };
  }

  return {
    icon: "border-amber-500/30 bg-amber-500/15 text-amber-400",
    bar: "bg-amber-400",
  };
};

const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-black px-8 py-6 text-white md:px-12">
    <div className="mx-auto flex max-w-[1200px] items-center justify-center py-28">
      <div className="text-center">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-white/15 border-t-[#D4AF37]" />
        <p className="mt-4 text-sm font-medium text-white/80">
          Loading experiment report
        </p>
      </div>
    </div>
  </div>
);

const ErrorState: React.FC<{ message: string }> = ({ message }) => (
  <div className="rounded-[14px] border border-red-500/20 bg-red-500/10 p-5">
    <div className="flex items-start gap-3">
      <AlertTriangle className="mt-0.5 h-5 w-5 text-red-400" />
      <div>
        <div className="text-sm font-semibold text-red-100">
          Unable to load report
        </div>
        <p className="mt-1 text-sm text-red-100/80">{message}</p>
      </div>
    </div>
  </div>
);

const EmptyState: React.FC<{ title: string; description: string }> = ({
  title,
  description,
}) => (
  <div className="rounded-[12px] border border-dashed border-white/10 bg-white/[0.02] px-5 py-6 text-center">
    <p className="text-sm font-medium text-white/75">{title}</p>
    <p className="mt-2 text-sm text-white/45">{description}</p>
  </div>
);

const InsightsPendingState: React.FC<{
  status: "notgenerated" | "generating";
  isRefreshing: boolean;
}> = ({ status, isRefreshing }) => {
  const isGenerating = status === "generating";

  return (
    <section className="rounded-[16px] border border-white/10 bg-white/[0.03] px-6 py-14">
      <div className="mx-auto max-w-[640px] text-center">
        {isGenerating ? (
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-2 border-white/15 border-t-[#D4AF37]" />
        ) : (
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37]">
            <Lightbulb size={20} />
          </div>
        )}
        <h2 className="mt-5 text-2xl font-semibold text-white">
          {isGenerating ? "Generating insights" : "Insights not generated"}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-white/60">
          {isGenerating
            ? "Falcon is still processing this experiment's insights. This page will refresh the insights status every 10 seconds."
            : "Insights have not been generated for this experiment yet. Once the analysis starts, this page will switch to the generating state automatically."}
        </p>
        <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/30 px-4 py-2 text-xs uppercase tracking-[0.18em] text-white/60">
          {isGenerating ? (
            <>
              <span className="h-2 w-2 rounded-full bg-[#D4AF37]" />
              {isRefreshing ? "Checking for updates" : "Refreshing every 10 seconds"}
            </>
          ) : (
            <>
              <Clock size={12} />
              Waiting for analysis to start
            </>
          )}
        </div>
      </div>
    </section>
  );
};

const getNameInitials = (value?: string | null) =>
  (value ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "FA";

const HelpfulVoteButtons: React.FC<{
  size?: "sm" | "md";
  variant?: "boxed" | "ghost";
}> = ({ size = "sm", variant = "boxed" }) => {
  const [vote, setVote] = React.useState<"up" | "down" | null>(null);
  const buttonSize = size === "md" ? "h-10 w-10" : "h-8 w-8";
  const iconSize = size === "md" ? 16 : 13;

  return (
    <div className="flex gap-2">
      {(["up", "down"] as const).map((direction) => (
        <button
          key={direction}
          type="button"
          onClick={() =>
            setVote((current) => (current === direction ? null : direction))
          }
          className={`flex ${buttonSize} items-center justify-center rounded-lg transition-all ${
            variant === "ghost"
              ? vote === direction
                ? direction === "up"
                  ? "bg-green-500/10 text-green-400"
                  : "bg-red-500/10 text-red-400"
                : "text-white/50 hover:bg-white/[0.04] hover:text-white/75"
              : vote === direction
                ? direction === "up"
                  ? "border border-green-500/50 bg-green-500/15 text-green-400"
                  : "border border-red-500/50 bg-red-500/15 text-red-400"
                : "border border-white/15 bg-white/[0.04] text-white/50 hover:border-white/25"
          }`}
        >
          {direction === "up" ? (
            <ThumbsUp size={iconSize} />
          ) : (
            <ThumbsDown size={iconSize} />
          )}
        </button>
      ))}
    </div>
  );
};

const SupportingResponsesPanel: React.FC<{
  items: ReportSupportingResponse[];
  emptyLabel: string;
}> = ({ items, emptyLabel }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="mt-4 border-t border-white/[0.05] pt-4">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="flex items-center gap-2 text-sm text-white/45 transition-colors hover:text-white/70"
      >
        <ChevronDown
          size={14}
          className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
        <MessageSquare size={13} />
        Supporting Responses ({items.length})
      </button>
      {isOpen ? (
        items.length > 0 ? (
          <div className="mt-3 space-y-3">
            {items.map((item) => (
              <div
                key={
                  item.response_id ??
                  `${item.user_name}-${item.title}-${item.description}`
                }
                className="rounded-[10px] border border-white/[0.06] bg-white/[0.03] p-4"
              >
                {item.user_name ? (
                  <div className="mb-1 text-[11px] uppercase tracking-widest text-white/35">
                    {item.user_name}
                  </div>
                ) : null}
                {item.title ? (
                  <div className="mb-1 text-sm font-medium text-white/80">
                    {item.title}
                  </div>
                ) : null}
                <p className="text-sm leading-relaxed text-white/65">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-sm text-white/35">{emptyLabel}</p>
        )
      ) : null}
    </div>
  );
};

const InsightSupportingResponsesPanel: React.FC<{
  items: ReportSupportingResponse[];
  emptyLabel: string;
}> = ({ items, emptyLabel }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="mt-5 border-t border-white/[0.06] pt-5">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="flex w-full items-center justify-between gap-4 text-left"
      >
        <div className="flex items-center gap-2 text-sm font-medium uppercase tracking-[0.16em] text-white/85">
          <Users size={14} className="text-[#0A78C9]" />
          <span>Supporting Responses ({items.length})</span>
        </div>
        {isOpen ? (
          <ChevronUp size={16} className="text-white/60" />
        ) : (
          <ChevronDown size={16} className="text-white/60" />
        )}
      </button>

      {isOpen ? (
        items.length > 0 ? (
          <div className="mt-5 space-y-3">
            {items.map((item) => {
              const displayName = item.user_name || item.title || "Participant";

              return (
                <div
                  key={
                    item.response_id ??
                    `${item.user_name}-${item.title}-${item.description}`
                  }
                  className="rounded-[14px] border border-white/[0.06] bg-white/[0.03] px-5 py-4"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-[#152031] text-sm font-semibold text-white/80">
                      {getNameInitials(displayName)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-base font-semibold text-white">
                        {displayName}
                      </div>
                      {item.question ? (
                        <div className="mt-1 text-xs text-white/45">
                          {item.question}
                        </div>
                      ) : null}
                      <p className="mt-2 text-sm italic leading-relaxed text-white/75">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="mt-4 text-sm text-white/35">{emptyLabel}</p>
        )
      ) : null}
    </div>
  );
};

const InterviewQuestionsCard: React.FC<{
  questions: ReportInterviewQuestion[];
}> = ({ questions }) => (
  <div className="rounded-[14px] border border-white/10 bg-white/[0.02] px-6 py-5">
    <div className="mb-4 flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/45">
      <MessageSquare size={12} />
      Interview Questions ({questions.length})
    </div>

    <div className="space-y-3">
      {questions.map((question) => (
        <div key={question.question_id} className="flex items-start gap-3">
          <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-white/40" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium leading-6 text-white/92">
                {question.question}
              </p>
              {question.type ? (
                <span className="rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[10px] font-medium text-white/50">
                  {question.type}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ResearchQuestionInsightCard: React.FC<{
  researchQuestion: ReportResearchQuestion;
}> = ({ researchQuestion }) => {
  const accent = getResearchQuestionAccentClasses(
    researchQuestion.sentiment_label,
  );

  return (
    <article
      className={`rounded-[14px] border border-white/10 border-l-4 ${accent.border} bg-white/[0.02]`}
    >
      <div className="px-6 py-5">
        <div className="flex items-start justify-between gap-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Brain size={16} className={accent.icon} />
              <span
                className={`text-sm font-semibold tracking-[0.01em] ${accent.title}`}
              >
                {toLabel(researchQuestion.answer_label)}
              </span>
            </div>
          </div>
          {researchQuestion.date_label ? (
            <span className="shrink-0 text-xs font-semibold text-white/55 sm:text-sm">
              {researchQuestion.date_label}
            </span>
          ) : null}
        </div>

        <p className="mt-4 text-sm leading-7 text-white/88 sm:text-[15px]">
          {researchQuestion.description || "No summary is available yet."}
        </p>

        {researchQuestion.ai_reasoning ? (
          <div className="mt-5 border-t border-white/[0.06] pt-5">
            <div className="rounded-[12px] border border-white/[0.06] bg-white/[0.03] p-4">
              <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/50">
                <Sparkles size={11} className="text-[#0A78C9]" />
                AI Reasoning
              </div>
              <p className="text-sm italic leading-7 text-white/70 sm:text-[15px]">
                {researchQuestion.ai_reasoning}
              </p>
            </div>
          </div>
        ) : null}

        <div className="mt-5 flex items-center justify-between border-t border-white/[0.06] pt-5">
          <span className="text-[10px] uppercase tracking-widest text-white/45">
            Was this answer helpful?
          </span>
          <HelpfulVoteButtons />
        </div>
      </div>
    </article>
  );
};

const AdditionalInsightCard: React.FC<{ insight: ReportAdditionalInsight }> = ({
  insight,
}) => {
  const accent = getInsightAccentClasses(insight.sentiment);

  return (
    <article
      className={`rounded-[16px] border border-white/10 border-l-4 ${accent.border} bg-white/[0.02]`}
    >
      <div className="px-8 py-7">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <Lightbulb size={18} className={accent.icon} />
              <h3 className={`text-[20px] font-semibold ${accent.title}`}>
                {insight.title}
              </h3>
            </div>
          </div>
          {insight.date_label ? (
            <span className="shrink-0 text-sm font-semibold text-white/70">
              {insight.date_label}
            </span>
          ) : null}
        </div>

        <p className="mt-5 text-[15px] leading-8 text-white/88">
          {insight.description}
        </p>

        <div className="mt-6 border-t border-white/[0.06] pt-5">
          <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-white/65">
            <Sparkles size={12} className="text-[#0A78C9]" />
            AI Reasoning
          </div>
          <p className="text-[15px] italic leading-8 text-white/74">
            {insight.ai_reasoning || "No AI reasoning available."}
          </p>
        </div>

        <div className="mt-6 flex items-center justify-between border-t border-white/[0.06] pt-5">
          <span className="text-[11px] uppercase tracking-[0.24em] text-white/62">
            Was this insight helpful?
          </span>
          <HelpfulVoteButtons size="md" variant="ghost" />
        </div>

        <InsightSupportingResponsesPanel
          items={insight.supporting_responses}
          emptyLabel="No supporting responses are available for this AI insight yet."
        />
      </div>
    </article>
  );
};

const ResearchQuestionsTab: React.FC<{
  results: ExperimentReportResultsModel;
}> = ({ results }) => {
  const [expandedQuestions, setExpandedQuestions] = React.useState<
    Record<string, boolean>
  >({});

  return (
    <div className="flex flex-col gap-4">
      {results.research_questions.length > 0 ? (
        results.research_questions.map((researchQuestion, index) => {
          const isOpen =
            expandedQuestions[researchQuestion.rq_id] ?? index === 0;
          const accent = getResearchQuestionAccentClasses(
            researchQuestion.sentiment_label,
          );

          return (
            <div key={researchQuestion.rq_id}>
              <button
                type="button"
                onClick={() =>
                  setExpandedQuestions((current) => ({
                    ...current,
                    [researchQuestion.rq_id]: !isOpen,
                  }))
                }
                className={`w-full rounded-[14px] border border-white/10 border-l-4 bg-white/[0.03] px-6 py-5 text-left transition-colors hover:bg-white/[0.05] ${accent.border}`}
              >
                <div className="flex items-start gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/40">
                        Research Question {index + 1}
                      </span>
                      <span className="rounded-full border border-white/15 px-3 py-1 text-[11px] text-white/45">
                        {toLabel(researchQuestion.answer_label)}
                      </span>
                      <span
                        className={`rounded-full border px-3 py-1 text-[11px] ${getSentimentClasses(researchQuestion.sentiment_label)}`}
                      >
                        {toLabel(researchQuestion.sentiment_label)}
                      </span>
                    </div>
                    <p className="mt-4 max-w-[1100px] text-base font-medium leading-7 text-white sm:text-lg lg:text-[1.35rem] lg:leading-8">
                      {researchQuestion.research_question}
                    </p>
                  </div>
                  <div className="mt-1 shrink-0 rounded-full border border-white/10 bg-white/[0.03] p-2 text-white/50">
                    {isOpen ? (
                      <ChevronUp size={16} className="shrink-0" />
                    ) : (
                      <ChevronDown size={16} className="shrink-0" />
                    )}
                  </div>
                </div>
              </button>
              {isOpen ? (
                <div
                  className={`ml-4 mt-2 space-y-3 border-l pl-5 ${accent.line}`}
                >
                  {researchQuestion.interview_questions.length > 0 ? (
                    <InterviewQuestionsCard
                      questions={researchQuestion.interview_questions}
                    />
                  ) : (
                    <EmptyState
                      title="No mapped interview questions yet"
                      description="This research question does not have any saved interview questions linked to it yet."
                    />
                  )}

                  <ResearchQuestionInsightCard
                    researchQuestion={researchQuestion}
                  />
                </div>
              ) : null}
            </div>
          );
        })
      ) : (
        <EmptyState
          title="No research question insights yet"
          description="Run the insights analysis after collecting responses to populate this section."
        />
      )}
    </div>
  );
};

const AIInsightsTab: React.FC<{ results: ExperimentReportResultsModel }> = ({
  results,
}) =>
  results.additional_insights.length > 0 ? (
    <div className="space-y-6">
      {results.additional_insights.map((insight) => (
        <AdditionalInsightCard key={insight.insight_id} insight={insight} />
      ))}
    </div>
  ) : (
    <EmptyState
      title="No AI insights yet"
      description="Run the insights analysis after collecting responses to populate this section."
    />
  );

const ResponsesTab: React.FC<{
  researchQuestions: ReportResearchQuestion[];
  demographicsQuestions: string[];
  participants?: SyntheticParticipantRow[];
  participantSegmentLabel?: string;
  participantTimeLabel?: string;
}> = ({
  researchQuestions,
  demographicsQuestions,
  participants = [],
  participantSegmentLabel = "Visit Type",
  participantTimeLabel = "Time",
}) => {
  const [section, setSection] = React.useState<
    "participants" | "demographics" | "interview"
  >(participants.length > 0 ? "participants" : "interview");
  const [interviewSearch, setInterviewSearch] = React.useState("");
  const [interviewPage, setInterviewPage] = React.useState(1);
  const interviewPageSize = 8;
  const [participantSearch, setParticipantSearch] = React.useState("");
  const [participantPage, setParticipantPage] = React.useState(1);
  const participantPageSize = 10;

  const interviewRows = React.useMemo(
    () =>
      researchQuestions.flatMap((researchQuestion) =>
        researchQuestion.interview_questions.map((question) => ({
          id: question.question_id,
          screen: question.screen_label,
          question: question.question,
          sentiment: researchQuestion.sentiment_label,
        })),
      ),
    [researchQuestions],
  );

  const filteredInterviewRows = React.useMemo(() => {
    const query = interviewSearch.trim().toLowerCase();

    if (!query) {
      return interviewRows;
    }

    return interviewRows.filter((row) =>
      [row.screen, row.question, getInterviewSentimentLabel(row.sentiment)]
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [interviewRows, interviewSearch]);

  const totalInterviewPages = Math.max(
    1,
    Math.ceil(filteredInterviewRows.length / interviewPageSize),
  );

  const paginatedInterviewRows = React.useMemo(() => {
    const startIndex = (interviewPage - 1) * interviewPageSize;
    return filteredInterviewRows.slice(
      startIndex,
      startIndex + interviewPageSize,
    );
  }, [filteredInterviewRows, interviewPage, interviewPageSize]);

  React.useEffect(() => {
    setInterviewPage(1);
  }, [interviewSearch]);

  React.useEffect(() => {
    if (interviewPage > totalInterviewPages) {
      setInterviewPage(totalInterviewPages);
    }
  }, [interviewPage, totalInterviewPages]);

  const filteredParticipants = React.useMemo(() => {
    const query = participantSearch.trim().toLowerCase();
    if (!query) {
      return participants;
    }
    return participants.filter((p) =>
      [p.id, p.name, p.visit_type, p.device, p.quote, p.sentiment]
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [participants, participantSearch]);

  const totalParticipantPages = Math.max(
    1,
    Math.ceil(filteredParticipants.length / participantPageSize),
  );

  const paginatedParticipants = React.useMemo(() => {
    const startIndex = (participantPage - 1) * participantPageSize;
    return filteredParticipants.slice(
      startIndex,
      startIndex + participantPageSize,
    );
  }, [filteredParticipants, participantPage, participantPageSize]);

  React.useEffect(() => {
    setParticipantPage(1);
  }, [participantSearch]);

  React.useEffect(() => {
    if (participantPage > totalParticipantPages) {
      setParticipantPage(totalParticipantPages);
    }
  }, [participantPage, totalParticipantPages]);

  const sections = [
    { id: "participants", label: "Participants", count: participants.length },
    {
      id: "demographics",
      label: "Demographics",
      count: demographicsQuestions.length,
    },
    { id: "interview", label: "Interview", count: interviewRows.length },
  ] as const;

  return (
    <div className="flex gap-5">
      <div className="w-[190px] shrink-0">
        <div className="mb-3 px-1 text-[10px] uppercase tracking-widest text-white/40">
          Sections
        </div>
        {sections.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setSection(item.id)}
            className={`mb-1 flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm transition-all ${
              section === item.id
                ? "border-l-2 border-[#D4AF37] bg-white/[0.06] text-white"
                : "text-white/50 hover:bg-white/[0.03] hover:text-white/80"
            }`}
          >
            <span>{item.label}</span>
            <span
              className={`rounded-full px-1.5 py-0.5 text-[11px] ${
                section === item.id
                  ? "bg-white/10 text-white/60"
                  : "text-white/30"
              }`}
            >
              {item.count}
            </span>
          </button>
        ))}
      </div>

      <div className="min-w-0 flex-1">
        {section === "participants" ? (
          participants.length > 0 ? (
            <div className="space-y-3">
              <div className="relative">
                <Search
                  size={16}
                  className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/35"
                />
                <input
                  type="search"
                  value={participantSearch}
                  onChange={(event) =>
                    setParticipantSearch(event.target.value)
                  }
                  placeholder="Search participants, devices, quotes..."
                  className="h-11 w-full rounded-[10px] border border-white/12 bg-white/[0.03] pl-11 pr-4 text-sm text-white outline-none transition-colors placeholder:text-white/35 focus:border-white/20"
                />
              </div>

              <div className="overflow-hidden rounded-[12px] border border-white/10 bg-white/[0.02]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      {[
                        "Participant",
                        "Age",
                        participantSegmentLabel,
                        "Device",
                        participantTimeLabel,
                        "Sentiment",
                        "Quote",
                      ].map((header) => (
                        <th
                          key={header}
                          className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-white/45"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedParticipants.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-white/[0.04] hover:bg-white/[0.02]"
                      >
                        <td className="px-4 py-4 align-top">
                          <div className="font-medium text-white/90">
                            {row.name}
                          </div>
                          <div className="text-[11px] text-white/40">
                            {row.id}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-top text-white/80">
                          {row.age}
                        </td>
                        <td className="px-4 py-4 align-top text-white/80">
                          {row.visit_type}
                        </td>
                        <td className="px-4 py-4 align-top text-white/80">
                          {row.device}
                        </td>
                        <td className="px-4 py-4 align-top text-white/80">
                          {row.completion_time}
                        </td>
                        <td className="px-4 py-4 align-top">
                          <span
                            className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-medium ${getSentimentClasses(row.sentiment)}`}
                          >
                            {getInterviewSentimentLabel(row.sentiment)}
                          </span>
                        </td>
                        <td className="max-w-[420px] px-4 py-4 align-top text-white/75">
                          <span className="italic">“{row.quote}”</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredParticipants.length > 0 ? (
                  <div className="flex items-center justify-between border-t border-white/[0.06] px-4 py-3 text-sm text-white/60">
                    <span>
                      Showing{" "}
                      {(participantPage - 1) * participantPageSize + 1}-
                      {Math.min(
                        participantPage * participantPageSize,
                        filteredParticipants.length,
                      )}{" "}
                      of {filteredParticipants.length}
                    </span>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setParticipantPage((current) =>
                            Math.max(1, current - 1),
                          )
                        }
                        disabled={participantPage === 1}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-white/50 transition-colors hover:bg-white/[0.04] hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
                      >
                        <ChevronLeft size={14} />
                      </button>

                      <span className="flex min-w-[34px] items-center justify-center rounded-md bg-[#D4AF37] px-2 py-1 text-xs font-semibold text-black">
                        {participantPage}
                      </span>

                      <button
                        type="button"
                        onClick={() =>
                          setParticipantPage((current) =>
                            Math.min(totalParticipantPages, current + 1),
                          )
                        }
                        disabled={participantPage === totalParticipantPages}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-white/50 transition-colors hover:bg-white/[0.04] hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
                      >
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              {filteredParticipants.length === 0 ? (
                <EmptyState
                  title="No participants found"
                  description="Try a different search term to find matching participants."
                />
              ) : null}
            </div>
          ) : (
            <EmptyState
              title="Participant-level response data is not available yet"
              description="This view is wired for participant data, but the current report payload does not include participant rows."
            />
          )
        ) : null}

        {section === "demographics" ? (
          demographicsQuestions.length > 0 ? (
            <div className="overflow-hidden rounded-[12px] border border-white/10">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    {["Question", "Top Response", "Count"].map((header) => (
                      <th
                        key={header}
                        className="px-4 py-3 text-left text-[10px] font-medium uppercase tracking-widest text-white/40"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {demographicsQuestions.map((question) => (
                    <tr
                      key={question}
                      className="border-b border-white/[0.04] hover:bg-white/[0.02]"
                    >
                      <td className="px-4 py-3 text-white/80">{question}</td>
                      <td className="px-4 py-3 text-white/45">N/A</td>
                      <td className="px-4 py-3 text-white/45">N/A</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              title="No demographic questions"
              description="There are no demographic questions available for this report."
            />
          )
        ) : null}

        {section === "interview" ? (
          interviewRows.length > 0 ? (
            <div className="space-y-3">
              <div className="relative">
                <Search
                  size={16}
                  className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/35"
                />
                <input
                  type="search"
                  value={interviewSearch}
                  onChange={(event) => setInterviewSearch(event.target.value)}
                  placeholder="Search responses..."
                  className="h-11 w-full rounded-[10px] border border-white/12 bg-white/[0.03] pl-11 pr-4 text-sm text-white outline-none transition-colors placeholder:text-white/35 focus:border-white/20"
                />
              </div>

              <div className="overflow-hidden rounded-[12px] border border-white/10 bg-white/[0.02]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      {["Screen", "Question", "Sentiment"].map((header) => (
                        <th
                          key={header}
                          className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-white/45"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedInterviewRows.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-white/[0.04] hover:bg-white/[0.02]"
                      >
                        <td className="px-4 py-4 font-medium text-white/88">
                          {row.screen}
                        </td>
                        <td className="px-4 py-4 text-white/88">
                          {row.question}
                        </td>
                        <td className="px-4 py-4">
                          <span
                            className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-medium ${getSentimentClasses(row.sentiment)}`}
                          >
                            {getInterviewSentimentLabel(row.sentiment)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredInterviewRows.length > 0 ? (
                  <div className="flex items-center justify-between border-t border-white/[0.06] px-4 py-3 text-sm text-white/60">
                    <span>
                      Showing{" "}
                      {(interviewPage - 1) * interviewPageSize + 1}-
                      {Math.min(
                        interviewPage * interviewPageSize,
                        filteredInterviewRows.length,
                      )}{" "}
                      of {filteredInterviewRows.length}
                    </span>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setInterviewPage((current) => Math.max(1, current - 1))
                        }
                        disabled={interviewPage === 1}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-white/50 transition-colors hover:bg-white/[0.04] hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
                      >
                        <ChevronLeft size={14} />
                      </button>

                      <span className="flex min-w-[34px] items-center justify-center rounded-md bg-[#D4AF37] px-2 py-1 text-xs font-semibold text-black">
                        {interviewPage}
                      </span>

                      <button
                        type="button"
                        onClick={() =>
                          setInterviewPage((current) =>
                            Math.min(totalInterviewPages, current + 1),
                          )
                        }
                        disabled={interviewPage === totalInterviewPages}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-white/50 transition-colors hover:bg-white/[0.04] hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
                      >
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              {filteredInterviewRows.length === 0 ? (
                <EmptyState
                  title="No interview responses found"
                  description="Try a different search term to find matching interview questions."
                />
              ) : null}
            </div>
          ) : (
            <EmptyState
              title="No interview questions"
              description="Interview questions will appear here after the feedback flow questions are linked to research questions."
            />
          )
        ) : null}
      </div>
    </div>
  );
};

const TelemetryTab: React.FC<{ results: ExperimentReportResultsModel }> = ({
  results,
}) => (
  <div className="space-y-4">
    <div className="grid grid-cols-3 gap-4">
      <div className="rounded-[12px] border border-white/10 bg-white/[0.03] p-5">
        <div className="mb-2 text-[10px] uppercase tracking-widest text-white/50">
          Completion Rate
        </div>
        <div className="text-3xl font-semibold text-white">
          {formatMetricValue(results.experiment_metrics.completion_rate, "%")}
        </div>
      </div>
      <div className="rounded-[12px] border border-white/10 bg-white/[0.03] p-5">
        <div className="mb-2 text-[10px] uppercase tracking-widest text-white/50">
          Avg. Time to Complete
        </div>
        <div className="text-3xl font-semibold text-white">
          {formatMetricValue(
            results.experiment_metrics.average_time_to_complete,
          )}
        </div>
      </div>
      <div className="rounded-[12px] border border-white/10 bg-white/[0.03] p-5">
        <div className="mb-2 text-[10px] uppercase tracking-widest text-white/50">
          Insufficient Evidence RQs
        </div>
        <div className="text-3xl font-semibold text-white">
          {formatMetricValue(
            results.experiment_metrics.insufficient_evidence_rq_count,
          )}
        </div>
      </div>
    </div>
    <EmptyState
      title="Telemetry details are not available in the current report payload"
      description="This tab is ready for telemetry fields once the backend exposes per-screen session and interaction metrics."
    />
  </div>
);

const ExperimentDetailsTab: React.FC<{
  details: ExperimentReportDetailsModel;
}> = ({ details }) => (
  <div className="flex flex-col gap-5">
    <div className="rounded-[14px] border border-white/10 bg-white/[0.02] p-6">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/20">
          <BookOpen size={15} className="text-blue-400" />
        </div>
        <span className="text-base font-semibold text-white">
          Business Context
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-12 gap-y-6">
        <div>
          <div className="mb-1.5 text-[10px] uppercase tracking-widest text-white/40">
            Experiment Name
          </div>
          <div className="text-sm text-white/80">
            {details.businessContext.experimentName || "N/A"}
          </div>
        </div>
        <div>
          <div className="mb-1.5 text-[10px] uppercase tracking-widest text-white/40">
            Business Goal
          </div>
          <div className="text-sm leading-relaxed text-white/80">
            {details.businessContext.businessGoal || "N/A"}
          </div>
        </div>
        <div>
          <div className="mb-1.5 text-[10px] uppercase tracking-widest text-white/40">
            Target Audience
          </div>
          <div className="text-sm text-white/80">
            {details.businessContext.targetAudience || "N/A"}
          </div>
        </div>
      </div>
    </div>

    <div className="rounded-[14px] border border-white/10 bg-white/[0.02] p-6">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/20">
          <Layers size={15} className="text-blue-400" />
        </div>
        <span className="text-base font-semibold text-white">
          Research Foundation
        </span>
      </div>
      <div className="flex flex-col gap-6">
        <div>
          <div className="mb-1.5 text-[10px] uppercase tracking-widest text-white/40">
            Experiment Type
          </div>
          <div className="text-sm text-white/80">
            {details.researchFoundation.experimentType}
          </div>
        </div>
        <div>
          <div className="mb-1.5 text-[10px] uppercase tracking-widest text-white/40">
            Research Objective
          </div>
          <div className="text-sm leading-relaxed text-white/80">
            {details.researchFoundation.researchObjective || "N/A"}
          </div>
        </div>
        <div>
          <div className="mb-2 text-[10px] uppercase tracking-widest text-white/40">
            Core Research Questions
          </div>
          {details.researchFoundation.coreResearchQuestions.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {details.researchFoundation.coreResearchQuestions.map(
                (question) => (
                  <li
                    key={question}
                    className="flex items-start gap-2.5 text-sm text-white/70"
                  >
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-white/30" />
                    {question}
                  </li>
                ),
              )}
            </ul>
          ) : (
            <p className="text-sm text-white/45">
              No research questions available.
            </p>
          )}
        </div>
        <div>
          <div className="mb-1.5 text-[10px] uppercase tracking-widest text-white/40">
            Primary Hypothesis
          </div>
          <div className="text-sm leading-relaxed text-white/80">
            {details.researchFoundation.primaryHypothesis || "N/A"}
          </div>
        </div>
        <div>
          <div className="mb-2 text-[10px] uppercase tracking-widest text-white/40">
            Key Assumptions
          </div>
          {details.researchFoundation.keyAssumptions.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {details.researchFoundation.keyAssumptions.map((assumption) => (
                <li
                  key={assumption}
                  className="flex items-start gap-2.5 text-sm text-white/70"
                >
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-white/30" />
                  {assumption}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-white/45">
              No key assumptions available.
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-x-12 border-t border-white/[0.06] pt-5">
          <div>
            <div className="mb-1.5 text-[10px] uppercase tracking-widest text-white/40">
              Success Signal (Qualitative)
            </div>
            <div className="text-sm leading-relaxed text-white/80">
              {details.researchFoundation.successSignalQualitative || "N/A"}
            </div>
          </div>
          <div>
            <div className="mb-1.5 text-[10px] uppercase tracking-widest text-white/40">
              Success Signal (Quantitative)
            </div>
            <div className="text-sm leading-relaxed text-white/80">
              {details.researchFoundation.successSignalQuantitative || "N/A"}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="rounded-[14px] border border-white/10 bg-white/[0.02] p-6">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/20">
          <Tag size={15} className="text-blue-400" />
        </div>
        <span className="text-base font-semibold text-white">
          Prototype & Tags
        </span>
      </div>
      <div className="flex flex-col gap-6">
        <div>
          <div className="mb-1.5 text-[10px] uppercase tracking-widest text-white/40">
            Screens
          </div>
          <div className="text-sm text-white/80">
            {details.prototypeAndTags.screens} screens
          </div>
        </div>
        <div>
          <div className="mb-1.5 text-[10px] uppercase tracking-widest text-white/40">
            Idea
          </div>
          <div className="text-sm leading-relaxed text-white/80">
            {details.prototypeAndTags.idea || "N/A"}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-12 border-t border-white/[0.06] pt-5">
          <div>
            <div className="mb-1.5 text-[10px] uppercase tracking-widest text-white/40">
              Tagged Screens
            </div>
            <div className="text-sm text-white/80">
              {details.prototypeAndTags.taggedScreens} screens
            </div>
          </div>
          <div>
            <div className="mb-1.5 text-[10px] uppercase tracking-widest text-white/40">
              Total Questions
            </div>
            <div className="text-sm text-white/80">
              {details.prototypeAndTags.totalQuestions} questions
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const ExperimentReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const experimentId = searchParams.get("id")?.trim() || undefined;
  const requestedName = searchParams.get("name")?.trim() || undefined;
  const [activeTab, setActiveTab] = React.useState<"results" | "details">(
    "results",
  );
  const [activeInsightTab, setActiveInsightTab] =
    React.useState<InsightTabId>("research-questions");
  const [yourDecision, setYourDecision] = React.useState("Pursue");
  const [decisionOpen, setDecisionOpen] = React.useState(false);
  const [helpfulVote, setHelpfulVote] = React.useState<"up" | "down" | null>(
    null,
  );

  const experimentQuery = useExperimentByIdQuery(experimentId);
  const insightsQuery = useExperimentInsights(experimentId);
  const prototypeQuery = usePrototypeByExperiment(experimentId, {
    enabled: Boolean(experimentId),
  });
  const iterationId = React.useMemo(
    () => resolvePrototypeIterationIdFromDetail(prototypeQuery.data),
    [prototypeQuery.data],
  );
  const feedbackFlowQuery = useFeedbackFlow(experimentId, iterationId, {
    enabled: Boolean(experimentId && iterationId),
  });

  const isLoading =
    Boolean(experimentId) &&
    (experimentQuery.isLoading || insightsQuery.isLoading);
  const realExperiment = experimentQuery.data ?? null;
  const realInsightsData = insightsQuery.data?.data ?? null;
  const realInsightsStatus = normalizeInsightsStatus(realInsightsData?.status);
  // Synthetic override only kicks in when (a) the experiment name matches a
  // registered demo entry AND (b) the real insights payload is missing or in a
  // non-generated state (insights query failed, returned no data, or returned
  // a not-generated/generating/unknown status). A real experiment with real
  // generated insights is never replaced.
  const realInsightsAreUsable =
    Boolean(realInsightsData) && realInsightsStatus === "generated";
  const syntheticOverride = React.useMemo(() => {
    if (insightsQuery.isLoading) {
      return null;
    }

    if (realInsightsAreUsable) {
      return null;
    }

    return getSyntheticInsightsForExperiment(
      requestedName ?? experimentQuery.data?.name ?? null,
    );
  }, [
    insightsQuery.isLoading,
    realInsightsAreUsable,
    requestedName,
    experimentQuery.data?.name,
  ]);
  // Use the synthetic experiment metadata fallback only when the real
  // experiment query returned no data AND the synthetic override carries a
  // fallback. Real API data always wins.
  const effectiveExperiment =
    realExperiment ?? syntheticOverride?.experimentDetailFallback ?? null;
  const usingSyntheticExperimentDetail =
    !realExperiment && Boolean(syntheticOverride?.experimentDetailFallback);
  const primaryError =
    (usingSyntheticExperimentDetail ? null : experimentQuery.error) ??
    (syntheticOverride ? null : insightsQuery.error);
  const insightsData = syntheticOverride
    ? syntheticOverride.insightsData
    : realInsightsData;
  const insightsStatus = normalizeInsightsStatus(insightsData?.status);
  const viewModel = React.useMemo(() => {
    const base = buildExperimentReportViewModel({
      displayName: requestedName,
      experiment: effectiveExperiment,
      insights: insightsData,
      feedbackFlow: feedbackFlowQuery.data?.feedbackFlow,
      prototype: prototypeQuery.data,
    });

    if (!syntheticOverride) {
      return base;
    }

    return {
      ...base,
      results: {
        ...base.results,
        decision_framework: {
          ...base.results.decision_framework,
          supporting_responses:
            syntheticOverride.decisionSupportingResponses ??
            base.results.decision_framework.supporting_responses,
        },
        experiment_metrics: {
          ...base.results.experiment_metrics,
          completion_rate: syntheticOverride.metricsOverride.completion_rate,
          average_time_to_complete:
            syntheticOverride.metricsOverride.average_time_to_complete,
        },
        research_questions: base.results.research_questions.map(
          (researchQuestion) => {
            const overrideQuestions =
              syntheticOverride.interviewQuestionsByRqId?.[
                researchQuestion.rq_id
              ];

            if (
              !overrideQuestions ||
              researchQuestion.interview_questions.length > 0
            ) {
              return researchQuestion;
            }

            return {
              ...researchQuestion,
              interview_questions: overrideQuestions,
            };
          },
        ),
      },
    };
  }, [
    requestedName,
    effectiveExperiment,
    insightsData,
    feedbackFlowQuery.data?.feedbackFlow,
    prototypeQuery.data,
    syntheticOverride,
  ]);

  const decisionOptions = ["Pursue", "Pivot", "Pause"];
  const decisionConfidence = Math.max(
    viewModel.results.decision_framework.pursue_probability,
    viewModel.results.decision_framework.pivot_probability,
    viewModel.results.decision_framework.pause_probability,
  );
  const decisionAccent = getDecisionAccentClasses(
    viewModel.results.decision_framework.decision_label,
  );
  const demographicsQuestions =
    feedbackFlowQuery.data?.feedbackFlow.questions
      .filter((question) => question.section === "demographics")
      .map((question) => question.question.trim())
      .filter(Boolean) ?? [];
  const showInsightsPendingState =
    !primaryError &&
    (!insightsData ||
      insightsStatus === "notgenerated" ||
      insightsStatus === "generating");

  if (!experimentId) {
    return (
      <div className="min-h-screen bg-black px-8 py-6 text-white md:px-12">
        <div className="mx-auto max-w-[1200px]">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-5 inline-flex items-center gap-2 text-white/70 hover:text-white"
          >
            <ArrowLeft size={16} />
            <span className="text-sm font-semibold">Back</span>
          </button>
          <EmptyState
            title="Experiment ID missing"
            description="Open this page from the dashboard or finalize step so the report knows which experiment to load."
          />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-black px-8 py-6 text-white md:px-12">
      <div className="container mx-auto max-w-[1200px]">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-5 inline-flex items-center gap-2 text-white/70 hover:text-white"
        >
          <ArrowLeft size={16} />
          <span className="text-sm font-semibold">Back</span>
        </button>

        {primaryError ? (
          <div className="mb-6">
            <ErrorState
              message={getExperimentApiErrorMessage(
                primaryError,
                "Unable to load this experiment report.",
              )}
            />
          </div>
        ) : null}

        <div className="mb-3 flex items-start justify-between">
          <h1 className="max-w-[900px] text-4xl font-bold tracking-tight text-white">
            {viewModel.header.title}
          </h1>
          <span
            className={`ml-4 inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1 text-xs ${getStatusClasses(
              viewModel.results.status,
            )}`}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {viewModel.header.statusLabel}
          </span>
        </div>

        <div className="mb-6 flex items-center gap-6 text-sm text-white/70">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-blue-500/30 text-xs font-semibold text-white">
              {viewModel.header.ownerInitials}
            </div>
            <span>{viewModel.header.ownerName}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <CalendarDays size={14} />
            Launched {viewModel.header.launchedDateLabel ?? "N/A"}
          </div>
          <div className="flex items-center gap-1.5">
            <CalendarDays size={14} />
            Completed {viewModel.header.completedDateLabel ?? "N/A"}
          </div>
        </div>

        <div className="mb-8 flex w-fit items-center gap-1 rounded-lg border border-white/10 p-1">
          {(["results", "details"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`rounded-md px-4 py-1.5 text-sm font-medium transition-all ${
                activeTab === tab
                  ? "bg-white/10 text-white"
                  : "text-white/50 hover:text-white/70"
              }`}
            >
              {tab === "results" ? "Results" : "Experiment Details"}
            </button>
          ))}
        </div>

        {activeTab === "details" ? (
          <ExperimentDetailsTab details={viewModel.details} />
        ) : showInsightsPendingState ? (
          <InsightsPendingState
            status={
              insightsStatus === "generating" ? "generating" : "notgenerated"
            }
            isRefreshing={insightsQuery.isFetching}
          />
        ) : (
          <>
            <section className="mb-8">
              <div className="mb-3 text-[10px] uppercase tracking-[0.18em] text-white/50">
                Goal
              </div>
              <div className="rounded-[12px] border border-white/10 bg-white/[0.03] p-5">
                <p className="text-sm leading-relaxed text-white/80">
                  {viewModel.results.goal || "Goal unavailable."}
                </p>
              </div>
            </section>

            <section className="mb-8">
              <div className="mb-3 text-[10px] uppercase tracking-[0.18em] text-white/50">
                Decision
              </div>
              <div className="rounded-[12px] border border-white/10 bg-white/[0.03] p-6">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full border ${decisionAccent.icon}`}
                    >
                      <CheckCircle2 size={16} />
                    </div>
                    <span className="text-base font-semibold text-white">
                      AI Recommendation:{" "}
                      {viewModel.results.decision_framework.decision_label}
                    </span>
                  </div>
                  <span className="text-sm text-white/60">
                    {formatPercent(decisionConfidence)} Decision Confidence
                  </span>
                </div>
                <div className="mb-5 h-1 overflow-hidden rounded-full bg-white/10">
                  <div
                    className={`h-full rounded-full ${decisionAccent.bar}`}
                    style={{ width: formatPercent(decisionConfidence) }}
                  />
                </div>
                {/* <div className="mb-5 grid grid-cols-3 gap-3">
                  {[
                    {
                      label: "Pursue",
                      value:
                        viewModel.results.decision_framework.pursue_probability,
                    },
                    {
                      label: "Pivot",
                      value:
                        viewModel.results.decision_framework.pivot_probability,
                    },
                    {
                      label: "Pause",
                      value:
                        viewModel.results.decision_framework.pause_probability,
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-[10px] border border-white/[0.06] bg-white/[0.03] p-4"
                    >
                      <div className="text-[10px] uppercase tracking-widest text-white/40">
                        {item.label}
                      </div>
                      <div className="mt-2 text-2xl font-semibold text-white">
                        {formatPercent(item.value)}
                      </div>
                    </div>
                  ))}
                </div> */}
                <div className="flex items-start gap-8">
                  <p className="min-h-[80px] flex-1 text-sm italic leading-relaxed text-white/70">
                    {viewModel.results.decision_framework
                      .decision_description ||
                      "No decision description available."}
                  </p>
                  <div className="w-[220px] shrink-0">
                    <div className="mb-2 text-[10px] uppercase tracking-widest text-white/40">
                      Your Decision
                    </div>
                    <div className="relative mb-5">
                      <button
                        type="button"
                        onClick={() => setDecisionOpen((current) => !current)}
                        className="flex w-full items-center justify-between rounded-lg border border-white/15 bg-white/[0.05] px-3 py-2.5 text-sm text-white"
                      >
                        {yourDecision}
                        <ChevronDown size={14} className="text-white/50" />
                      </button>
                      {decisionOpen ? (
                        <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-lg border border-white/15 bg-[#0f1623] shadow-2xl">
                          {decisionOptions.map((option) => (
                            <button
                              key={option}
                              type="button"
                              onClick={() => {
                                setYourDecision(option);
                                setDecisionOpen(false);
                              }}
                              className="flex w-full items-center justify-between px-3 py-2.5 text-sm text-white/80 transition-colors hover:bg-white/[0.06]"
                            >
                              {option}
                              {yourDecision === option ? (
                                <Check size={14} className="text-white/70" />
                              ) : null}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div className="mb-2 text-[10px] uppercase tracking-widest text-white/40">
                      Was this helpful?
                    </div>
                    <div className="flex gap-2">
                      {(["up", "down"] as const).map((direction) => (
                        <button
                          key={direction}
                          type="button"
                          onClick={() =>
                            setHelpfulVote((current) =>
                              current === direction ? null : direction,
                            )
                          }
                          className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-all ${
                            helpfulVote === direction
                              ? direction === "up"
                                ? "border-green-500/50 bg-green-500/15 text-green-400"
                                : "border-red-500/50 bg-red-500/15 text-red-400"
                              : "border-white/15 bg-white/[0.04] text-white/50 hover:border-white/25"
                          }`}
                        >
                          {direction === "up" ? (
                            <ThumbsUp size={15} />
                          ) : (
                            <ThumbsDown size={15} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <SupportingResponsesPanel
                  items={
                    viewModel.results.decision_framework.supporting_responses
                  }
                  emptyLabel="No supporting responses are available for the final decision yet."
                />
              </div>
            </section>

            <section className="mb-8">
              <div className="mb-3 text-[10px] uppercase tracking-[0.18em] text-white/50">
                Experiment Metrics
              </div>
              <div className="grid grid-cols-4 gap-4">
                {[
                  {
                    icon: <Sparkles size={13} className="text-[#0A78C9]" />,
                    label: "Outcome Summary",
                    value: formatMetricValue(
                      viewModel.results.experiment_metrics.outcome_summary,
                    ),
                  },
                  {
                    icon: <Users size={13} className="text-[#0A78C9]" />,
                    label: "Responses",
                    value: formatMetricValue(
                      viewModel.results.experiment_metrics.responses,
                    ),
                  },
                  {
                    icon: <CheckCircle2 size={13} className="text-[#0A78C9]" />,
                    label: "Completion Rate",
                    value: formatMetricValue(
                      viewModel.results.experiment_metrics.completion_rate,
                      "%",
                    ),
                  },
                  {
                    icon: <Clock size={13} className="text-[#0A78C9]" />,
                    label: "Avg. Time to Complete",
                    value: formatMetricValue(
                      viewModel.results.experiment_metrics
                        .average_time_to_complete,
                    ),
                  },
                ].map((card) => (
                  <div
                    key={card.label}
                    className="rounded-[12px] border border-white/10 bg-white/[0.03] p-5"
                  >
                    <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/50">
                      {card.icon}
                      {card.label}
                    </div>
                    <div className="text-4xl font-semibold text-white">
                      {card.value}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-sm text-white/45">
                {formatMetricValue(
                  viewModel.results.experiment_metrics
                    .insufficient_evidence_rq_count,
                )}{" "}
                research questions currently have insufficient evidence.
              </p>
            </section>

            <div className="mb-5 flex items-center justify-between">
              <div className="flex w-fit items-center gap-1 rounded-xl border border-white/10 bg-white/[0.02] p-1">
                {[
                  {
                    key: "research-questions",
                    icon: <Brain size={14} />,
                    label: "Research Questions",
                  },
                  {
                    key: "ai-insights",
                    icon: <Lightbulb size={14} />,
                    label: "AI Insights",
                  },
                  {
                    key: "responses",
                    icon: <MessageSquare size={13} />,
                    label: "Responses",
                  },
                  {
                    key: "telemetry",
                    icon: <Activity size={14} />,
                    label: "Telemetry",
                  },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveInsightTab(tab.key as InsightTabId)}
                    className={`flex items-center gap-2 rounded-lg border px-4 py-1.5 text-sm font-medium transition-all ${
                      activeInsightTab === tab.key
                        ? "border-[#D4AF37]/80 bg-white/[0.08] text-white shadow-[inset_0_0_0_1px_rgba(212,175,55,0.18)]"
                        : "border-transparent text-white/55 hover:bg-white/[0.04] hover:text-white/80"
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="flex items-center gap-2 rounded-lg border border-white/10 px-4 py-1.5 text-sm text-white/60 transition-colors hover:text-white/80"
              >
                <Download size={14} />
                Export
              </button>
            </div>

            {activeInsightTab === "research-questions" ? (
              <ResearchQuestionsTab results={viewModel.results} />
            ) : null}
            {activeInsightTab === "ai-insights" ? (
              <AIInsightsTab results={viewModel.results} />
            ) : null}
            {activeInsightTab === "responses" ? (
              <ResponsesTab
                researchQuestions={viewModel.results.research_questions}
                demographicsQuestions={demographicsQuestions}
                participants={syntheticOverride?.participants}
                participantSegmentLabel={
                  syntheticOverride?.participantSegmentLabel
                }
                participantTimeLabel={syntheticOverride?.participantTimeLabel}
              />
            ) : null}
            {activeInsightTab === "telemetry" ? (
              <TelemetryTab results={viewModel.results} />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default ExperimentReportPage;
